import { create } from 'zustand'
import { eventBus } from '@/lib/eventBus/events'
import { db } from '@/lib/db/database'

interface BookingStore {
  pendingIds: Set<string>       // documentIds currently in the sync queue
  addPending: (id: string) => void
  removePending: (id: string) => void
  isPending: (id: string) => boolean
}

export const useBookingStore = create<BookingStore>((set, get) => ({
  pendingIds: new Set(),

  addPending: (id) =>
    set((s) => {
      const next = new Set(s.pendingIds)
      next.add(id)
      return { pendingIds: next }
    }),

  removePending: (id) =>
    set((s) => {
      const next = new Set(s.pendingIds)
      next.delete(id)
      return { pendingIds: next }
    }),

  isPending: (id) => get().pendingIds.has(id),
}))

// ── Module-level event wiring — fires once on import ─────────────────────────

// Mark booking as pending when queued
eventBus.on('booking:queued', ({ tempId }) => {
  useBookingStore.getState().addPending(tempId)
})

// When a POST syncs: replace the temp Dexie record with the real one
// useLiveQuery will automatically re-render when Dexie changes
eventBus.on('booking:synced', async ({ tempId, serverDocumentId }) => {
  useBookingStore.getState().removePending(tempId)
  if (!serverDocumentId) return
  const tempRecord = await db.bookings.get(tempId)
  if (tempRecord) {
    await db.bookings.delete(tempId)
    await db.bookings.put({ ...tempRecord, documentId: serverDocumentId })
  }
  useBookingStore.getState().removePending(serverDocumentId)
})

// On failure: remove the temp record from Dexie
// useLiveQuery will automatically re-render when Dexie changes
eventBus.on('booking:sync-failed', async ({ tempId }) => {
  useBookingStore.getState().removePending(tempId)
  await db.bookings.delete(tempId)
})
